import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Single source of truth for page-level SEO / OG / Twitter / JSON-LD.
 *
 * title format: `${title} | CultureCareers` (if title provided)
 * description: 140–155 chars, per page, human-written
 * image: 1200×630 editorial cover
 */
const BASE_URL = 'https://culturecareers.org';
const SITE_NAME = 'CultureCareers';
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-cover.png`;
const GSC_TOKEN = process.env.REACT_APP_GSC_VERIFICATION_TOKEN;

const SEO = ({
  title,
  description,
  path,
  type = 'website',
  image,
  jsonLd,
  orgName
}) => {
  const fullTitle = title
    ? `${title} | ${SITE_NAME}`
    : `Arts and Culture Jobs in India | ${SITE_NAME}`;
  const fullUrl = path ? `${BASE_URL}${path}` : BASE_URL;
  const defaultDesc =
    "Curated jobs, gigs, residencies, grants, and creative opportunities across India's arts, culture, museums and design sector.";
  const desc = description || defaultDesc;
  const ogImage = image || DEFAULT_OG_IMAGE;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={fullUrl} />

      {GSC_TOKEN && <meta name="google-site-verification" content={GSC_TOKEN} />}

      {/* Open Graph — WhatsApp, LinkedIn, Facebook, iMessage */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_IN" />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:secure_url" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title || SITE_NAME} />
      {orgName && <meta property="og:see_also" content={orgName} />}

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={title || SITE_NAME} />

      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
};

export default SEO;
